import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useDispatch } from "react-redux";
import {
  Card,
  CardInner,
  DetailGrid,
  DetailLeft,
  DetailRight,
  DetailSection,
  DetailSubtitle,
  DetailTitle,
  FeaturedInLogo,
  FeaturedInLogoItem,
  FeaturedInLogoLink,
  FeaturedInLogoText,
  FeaturedInLogos,
  FeaturedInSection,
  FeaturedInTitle,
  HeroActions,
  HeroCard,
  HeroCopy,
  HeroCtaButton,
  HeroDescription,
  HeroImage,
  HeroImageContainer,
  HeroTitle,
  Inner,
  LeadFormBody,
  LeadFormCard,
  LeadFormDescription,
  LeadFormErrorIcon,
  LeadFormErrorRow,
  LeadFormErrorText,
  LeadFormField,
  LeadFormFieldLabel,
  LeadFormFields,
  LeadFormHeader,
  LeadFormHeaderTitle,
  LeadFormPhoneInput,
  LeadFormResetButton,
  LeadFormSubmitButton,
  LeadFormSuccess,
  LeadFormSuccessIcon,
  LeadFormSuccessText,
  LeadFormSuccessTitle,
  LeadFormTextField,
  Main,
  NotFoundDescription,
  NotFoundTitle,
  QuoteAuthor,
  QuoteBlock,
  QuoteText,
  ReportHelpsImage,
  ReportHelpsInner,
  ReportHelpsItem,
  ReportHelpsItemDescription,
  ReportHelpsItemTitle,
  ReportHelpsList,
  ReportHelpsSection,
  ReportHelpsSubtitle,
  ReportHelpsTitle,
  ReportMattersIcon,
  ReportMattersItem,
  ReportMattersList,
  ReportMattersSection,
  ReportMattersText,
  ReportMattersTitle,
  RevealDescription,
  RevealDivider,
  RevealItem,
  RevealList,
  RevealValue,
} from "../../../components/employer/MarketInsightDetailPage/styles";
import { attachMarketInsightBySlug } from "../../../components/employer/MarketInsightsPage/reportRouting";
import ToResolveClassNameError from "../../../components/hoc/ToResolveClassNameError";
import EmployerNavbar from "../../../components/shared/SharedNavbar/Navbar/EmployerNavbar";
import * as config from "../../../config/config";
import { fetchMarketInsightBySlug } from "../../../redux/actions/market_action";
import {
  sendCompanyLeadEnquiryForm,
  sendVisitorLeadEnquiryForm,
} from "../../../redux/actions/user_action";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function MarketInsightDetailPage() {
  const router = useRouter();
  const dispatch = useDispatch();
  const [marketInsightData, setMarketInsightData] = useState({});
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isUpLg = useMediaQuery(theme.breakpoints.up("lg"));
  const slug = router.query.slug;
  const leadFormRoleOptions = [
    { value: "business_owner", label: "Business Owner / CEO" },
    { value: "hr", label: "HR / Talent Acquisition" },
    { value: "hiring_manager", label: "Hiring Manager" },
    { value: "job_seeker", label: "Job Seeker / Student" },
    { value: "other", label: "Other" },
  ];

  async function fetchData() {
    try {
      setLoading(true);
      const { payload } = await dispatch(fetchMarketInsightBySlug(slug));
      setMarketInsightData({
        ...payload,
      });
    } catch (error) {
      setMarketInsightData({});
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!router.isReady || !slug) return;
    fetchData();
  }, [router.isReady, slug]);

  const report = useMemo(
    () => attachMarketInsightBySlug(marketInsightData ?? []),
    [marketInsightData]
  );

  const metaTitle =
    report?.heroSectionHeader || "Market Insights | Hiredly Malaysia";
  const metaDescription = report?.description;
  const canonicalHref = slug
    ? `https://my.hiredly.com/employer/market-insights/${encodeURIComponent(
        slug
      )}`
    : "https://my.hiredly.com/employer/market-insights";

  const heroSectionHeader =
    report?.heroSectionHeader || report?.nameOfWhitePaper || "";
  const heroDescription = report?.heroDescription || report?.description || "";
  const heroImageSrc =
    report?.heroImageSrc?.data?.attributes?.url ||
    `${config.assetDomain}/images/employer_landing/work-culture-report.png`;
  const ctaLabel =
    report?.ctaLabel ||
    (report?.nameOfWhitePaper ? `Get the ${report.nameOfWhitePaper}` : "Get");
  const ctaUrl = report?.ctaUrl || "";
  const reportAsFeaturedIn = report?.asFeaturedIn || null;
  const featuredInEnabled = Boolean(reportAsFeaturedIn?.enabled);
  const featuredInTitle = reportAsFeaturedIn?.title ?? "As Featured In";
  const featuredInItems = featuredInEnabled
    ? reportAsFeaturedIn?.items ?? []
    : [];

  const dataReveals = report?.dataReveals || {};
  const dataRevealsTitle = dataReveals?.title || "";
  const dataRevealsSubtitle = dataReveals?.subtitle || "";
  const dataRevealsItems = dataReveals?.items || [];

  const leadForm = report?.leadForm || {};
  const leadFormTitle =
    leadForm?.title ||
    (report?.nameOfWhitePaper ? `Get the ${report.nameOfWhitePaper}` : "");
  const leadFormDescription =
    leadForm?.description || "Fill out the form below to get instant access.";
  const leadFormSubmitLabel = leadForm?.submitLabel || "Email Me the Report";
  const leadFormRef = useRef(null);
  const leadFormSuccessIconSrc =
    config.assetDomain +
    "/images/employer_landing/market-insights/success_animation.json";

  const [leadFormValues, setLeadFormValues] = useState({
    fullName: "",
    phoneNumber: undefined,
    workEmail: "",
    companyName: "",
    role: "",
    roleOther: "",
  });
  const [leadFormTouched, setLeadFormTouched] = useState({
    fullName: false,
    phoneNumber: false,
    workEmail: false,
    companyName: false,
    role: false,
    roleOther: false,
  });
  const [leadFormErrors, setLeadFormErrors] = useState({});
  const [leadFormSubmitting, setLeadFormSubmitting] = useState(false);
  const [leadFormSubmitted, setLeadFormSubmitted] = useState(false);
  const hasPhoneNumberError = Boolean(
    leadFormTouched.phoneNumber && leadFormErrors.phoneNumber
  );
  const isOtherRole = leadFormValues.role === "other";

  const resetLeadForm = () => {
    setLeadFormSubmitted(false);
    setLeadFormValues({
      fullName: "",
      phoneNumber: undefined,
      workEmail: "",
      companyName: "",
      role: "",
      roleOther: "",
    });
    setLeadFormTouched({
      fullName: false,
      phoneNumber: false,
      workEmail: false,
      companyName: false,
      role: false,
      roleOther: false,
    });
    setLeadFormErrors({});
  };

  const validateLeadForm = (values) => {
    const nextErrors = {};
    const fullName = (values.fullName || "").trim();
    const workEmail = (values.workEmail || "").trim();
    const companyName = (values.companyName || "").trim();
    const phoneNumber = values.phoneNumber;
    const role = (values.role || "").trim();
    const roleOther = (values.roleOther || "").trim();

    if (!fullName) nextErrors.fullName = "This field is required.";
    if (!phoneNumber) nextErrors.phoneNumber = "This field is required.";
    else if (!isValidPhoneNumber(phoneNumber))
      nextErrors.phoneNumber = "Please enter a valid phone number.";
    if (!workEmail) nextErrors.workEmail = "This field is required.";
    else if (!emailRe.test(workEmail))
      nextErrors.workEmail = "Please enter a valid email address.";
    if (!companyName) nextErrors.companyName = "This field is required.";
    if (!role) nextErrors.role = "Please select an option.";
    if (role === "other" && !roleOther)
      nextErrors.roleOther = "This field is required.";

    return nextErrors;
  };

  const handleLeadFormSubmit = async (e) => {
    e.preventDefault();
    if (leadFormSubmitting) return;

    const nextTouched = {
      fullName: true,
      phoneNumber: true,
      workEmail: true,
      companyName: true,
      role: true,
      roleOther: isOtherRole,
    };
    setLeadFormTouched(nextTouched);

    const nextErrors = validateLeadForm(leadFormValues);
    setLeadFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    setLeadFormSubmitting(true);
    try {
      if (leadFormValues.role === "job_seeker") {
        try {
          await dispatch(
            sendVisitorLeadEnquiryForm({
              input: {
                fullName: leadFormValues.fullName,
                email: leadFormValues.workEmail,
                phoneNumber: leadFormValues.phoneNumber,
                segment: slug ? `${slug}` : "elp_market_insights",
                leadSource: "elp_market_insights",
              },
            })
          );
        } catch (error) {
          console.error("Failed to send visitor lead enquiry form:", error);
        }
      } else {
        try {
          await dispatch(
            sendCompanyLeadEnquiryForm({
              input: {
                fullName: leadFormValues.fullName,
                email: leadFormValues.workEmail,
                phoneNumber: leadFormValues.phoneNumber,
                companyName: leadFormValues.companyName,
                segment: slug ? `${slug}` : "elp_market_insights",
                leadSource: "elp_market_insights",
              },
            })
          );
        } catch (error) {
          console.error("Failed to send company lead enquiry form:", error);
        }
      }

      setLeadFormSubmitted(true);
      if (leadForm?.redirectUrl) {
        window.open(leadForm.redirectUrl, "_blank", "noopener,noreferrer");
      }
    } finally {
      setLeadFormSubmitting(false);
    }
  };

  const reportHelps = report?.reportHelpsYouDecide || {};
  const reportHelpsTitle = reportHelps?.title || "";
  const reportHelpsSubtitle = reportHelps?.subtitle || "";
  const reportHelpsItems = reportHelps?.items || [];

  const whyReportMatters = report?.whyReportMatters || {};
  const whyReportMattersTitle = whyReportMatters?.title || "";
  const whyReportMattersItems = whyReportMatters?.items || [];
  const quote = report?.quote || {};
  const quoteText = quote?.text || "";
  const quoteAuthor = quote?.author || "";

  const handleCtaClick = () => {
    if (ctaUrl && typeof window !== "undefined") {
      window.open(ctaUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (leadFormRef.current) {
      const elementTop =
        leadFormRef.current.getBoundingClientRect().top + window.scrollY;

      window.scrollTo({
        top: Math.max(0, elementTop - 150),
        behavior: "smooth",
      });
    }
  };

  if (loading || !router.isReady) {
    return (
      <>
        <Head>
          <title>{metaTitle}</title>
          <meta name="title" content={metaTitle} />
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalHref} />
        </Head>
        <ToResolveClassNameError>
          <EmployerNavbar />
          <Toolbar />

          <Main>
            <Inner>
              <Card elevation={0}>
                <CardInner>
                  <NotFoundTitle>Loading…</NotFoundTitle>
                </CardInner>
              </Card>
            </Inner>
          </Main>
        </ToResolveClassNameError>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalHref} />
      </Head>

      <ToResolveClassNameError>
        <EmployerNavbar />
        <Toolbar />

        <Main>
          <Inner>
            {report ? (
              <>
                <HeroCard elevation={0}>
                  <HeroCopy>
                    <HeroTitle>{heroSectionHeader}</HeroTitle>
                    <HeroDescription>{heroDescription}</HeroDescription>
                    <HeroActions>
                      <HeroCtaButton
                        onClick={handleCtaClick}
                        disabled={!ctaUrl && !leadFormTitle}
                      >
                        {ctaLabel}
                      </HeroCtaButton>
                    </HeroActions>
                  </HeroCopy>

                  <HeroImageContainer>
                    <HeroImage src={heroImageSrc} alt={heroSectionHeader} />
                  </HeroImageContainer>
                </HeroCard>

                {featuredInEnabled && featuredInItems.length ? (
                  <FeaturedInSection>
                    <FeaturedInTitle>{featuredInTitle}</FeaturedInTitle>
                    <FeaturedInLogos>
                      {featuredInItems.map((item, index) => {
                        const href =
                          item?.clickThroughURL ||
                          item?.link ||
                          item?.href ||
                          undefined;
                        const image =
                          item?.brandLogo?.data?.attributes?.url ||
                          item?.image?.data?.attributes?.url ||
                          item?.src ||
                          "";
                        const label =
                          item?.brandLogo?.data?.attributes?.alternativeText ||
                          item?.label ||
                          item?.name ||
                          `Featured ${index + 1}`;

                        const LogoEl = image ? (
                          <FeaturedInLogo src={image} alt={label} />
                        ) : (
                          <FeaturedInLogoText>{label}</FeaturedInLogoText>
                        );

                        return (
                          <FeaturedInLogoItem key={`${label}-${index}`}>
                            {href ? (
                              <FeaturedInLogoLink
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {LogoEl}
                              </FeaturedInLogoLink>
                            ) : (
                              <div>{LogoEl}</div>
                            )}
                          </FeaturedInLogoItem>
                        );
                      })}
                    </FeaturedInLogos>
                  </FeaturedInSection>
                ) : null}

                {dataRevealsTitle ||
                leadFormTitle ||
                dataRevealsItems.length ||
                reportHelpsTitle ||
                reportHelpsItems.length ||
                whyReportMattersTitle ||
                whyReportMattersItems.length ? (
                  <DetailSection>
                    <DetailGrid>
                      <DetailLeft>
                        {dataRevealsTitle ? (
                          <DetailTitle>{dataRevealsTitle}</DetailTitle>
                        ) : null}
                        {dataRevealsSubtitle ? (
                          <DetailSubtitle>{dataRevealsSubtitle}</DetailSubtitle>
                        ) : null}

                        {dataRevealsItems.length ? (
                          <RevealList>
                            {dataRevealsItems.map((item, index) => {
                              const value = item?.value || "";
                              const description = item?.description || "";

                              return (
                                <div key={`${value}-${index}`}>
                                  <RevealItem>
                                    {value ? (
                                      <RevealValue>{value}</RevealValue>
                                    ) : null}
                                    {description ? (
                                      <RevealDescription>
                                        {description}
                                      </RevealDescription>
                                    ) : null}
                                  </RevealItem>
                                  {index !== dataRevealsItems.length - 1 ? (
                                    <RevealDivider />
                                  ) : null}
                                </div>
                              );
                            })}
                          </RevealList>
                        ) : null}

                        {reportHelpsTitle || reportHelpsItems.length ? (
                          <ReportHelpsSection>
                            <ReportHelpsInner>
                              {reportHelpsTitle ? (
                                <ReportHelpsTitle>
                                  {reportHelpsTitle}
                                </ReportHelpsTitle>
                              ) : null}
                              {reportHelpsSubtitle ? (
                                <ReportHelpsSubtitle>
                                  {reportHelpsSubtitle}
                                </ReportHelpsSubtitle>
                              ) : null}

                              {reportHelpsItems.length ? (
                                <ReportHelpsList>
                                  {reportHelpsItems.map((item, index) => {
                                    const title = item?.title || "";
                                    const description = item?.description || "";
                                    const image =
                                      item?.image ||
                                      item?.imageUrl?.data?.attributes?.url ||
                                      "";

                                    if (!title && !description && !image)
                                      return null;

                                    return (
                                      <ReportHelpsItem
                                        key={`${title || "item"}-${index}`}
                                      >
                                        {image ? (
                                          <ReportHelpsImage
                                            src={image}
                                            alt={title}
                                          />
                                        ) : (
                                          <span />
                                        )}
                                        <div>
                                          {title ? (
                                            <ReportHelpsItemTitle>
                                              {title}
                                            </ReportHelpsItemTitle>
                                          ) : null}
                                          {description ? (
                                            <ReportHelpsItemDescription>
                                              {description}
                                            </ReportHelpsItemDescription>
                                          ) : null}
                                        </div>
                                      </ReportHelpsItem>
                                    );
                                  })}
                                </ReportHelpsList>
                              ) : null}
                            </ReportHelpsInner>
                          </ReportHelpsSection>
                        ) : null}

                        {whyReportMattersTitle ||
                        whyReportMattersItems.length ? (
                          <ReportMattersSection>
                            {whyReportMattersTitle ? (
                              <ReportMattersTitle>
                                {whyReportMattersTitle}
                              </ReportMattersTitle>
                            ) : null}

                            {whyReportMattersItems.length ? (
                              <ReportMattersList>
                                {whyReportMattersItems.map((item, index) => {
                                  const emphasis = item?.emphasis || "";
                                  const text = item?.text || "";

                                  if (!emphasis && !text) return null;

                                  return (
                                    <ReportMattersItem
                                      key={`${emphasis || "item"}-${index}`}
                                    >
                                      <ReportMattersIcon />
                                      <ReportMattersText>
                                        {emphasis ? (
                                          <strong>{emphasis}</strong>
                                        ) : null}{" "}
                                        {text}
                                      </ReportMattersText>
                                    </ReportMattersItem>
                                  );
                                })}
                              </ReportMattersList>
                            ) : null}
                          </ReportMattersSection>
                        ) : null}
                      </DetailLeft>

                      <DetailRight>
                        {leadFormTitle ? (
                          <>
                            <LeadFormCard elevation={0} ref={leadFormRef}>
                              <LeadFormHeader>
                                <LeadFormHeaderTitle>
                                  {leadFormTitle}
                                </LeadFormHeaderTitle>
                              </LeadFormHeader>
                              <LeadFormBody>
                                {leadFormSubmitted ? (
                                  <LeadFormSuccess>
                                    <LeadFormSuccessIcon
                                      path={leadFormSuccessIconSrc}
                                      play
                                      loop={true}
                                    />
                                    <LeadFormSuccessTitle>
                                      Your report is ready!
                                    </LeadFormSuccessTitle>
                                    <LeadFormSuccessText>
                                      We’ve opened the report in a new tab for
                                      you.
                                      <br />
                                      {isDownSm || isUpLg ? <br /> : null}
                                      If you don’t see it, please check your
                                      browser tabs or allow pop-ups.
                                    </LeadFormSuccessText>
                                    <LeadFormResetButton
                                      type="button"
                                      onClick={resetLeadForm}
                                    >
                                      Submit Another Request
                                    </LeadFormResetButton>
                                  </LeadFormSuccess>
                                ) : (
                                  <>
                                    <LeadFormDescription>
                                      {leadFormDescription}
                                    </LeadFormDescription>

                                    <form
                                      noValidate
                                      onSubmit={handleLeadFormSubmit}
                                    >
                                      <LeadFormFields>
                                        <LeadFormField>
                                          <LeadFormFieldLabel htmlFor="fullName">
                                            Full Name
                                          </LeadFormFieldLabel>
                                          <LeadFormTextField
                                            id="fullName"
                                            name="fullName"
                                            value={leadFormValues.fullName}
                                            onChange={(e) => {
                                              setLeadFormValues((prev) => ({
                                                ...prev,
                                                fullName: e.target.value,
                                              }));
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                fullName: undefined,
                                              }));
                                            }}
                                            onBlur={() => {
                                              setLeadFormTouched((prev) => ({
                                                ...prev,
                                                fullName: true,
                                              }));
                                              const fullName = (
                                                leadFormValues.fullName || ""
                                              ).trim();
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                fullName: fullName
                                                  ? undefined
                                                  : "This field is required.",
                                              }));
                                            }}
                                            required
                                            error={Boolean(
                                              leadFormTouched.fullName &&
                                                leadFormErrors.fullName
                                            )}
                                            fullWidth
                                            size="small"
                                          />
                                          {leadFormTouched.fullName &&
                                          leadFormErrors.fullName ? (
                                            <LeadFormErrorRow>
                                              <LeadFormErrorIcon />
                                              <LeadFormErrorText>
                                                {leadFormErrors.fullName}
                                              </LeadFormErrorText>
                                            </LeadFormErrorRow>
                                          ) : null}
                                        </LeadFormField>

                                        <LeadFormField>
                                          <LeadFormFieldLabel htmlFor="workEmail">
                                            Email
                                          </LeadFormFieldLabel>
                                          <LeadFormTextField
                                            id="workEmail"
                                            name="workEmail"
                                            type="email"
                                            value={leadFormValues.workEmail}
                                            onChange={(e) => {
                                              setLeadFormValues((prev) => ({
                                                ...prev,
                                                workEmail: e.target.value,
                                              }));
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                workEmail: undefined,
                                              }));
                                            }}
                                            onBlur={() => {
                                              setLeadFormTouched((prev) => ({
                                                ...prev,
                                                workEmail: true,
                                              }));
                                              const workEmail = (
                                                leadFormValues.workEmail || ""
                                              ).trim();
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                workEmail: !workEmail
                                                  ? "This field is required."
                                                  : emailRe.test(workEmail)
                                                  ? undefined
                                                  : "Please enter a valid email address.",
                                              }));
                                            }}
                                            required
                                            error={Boolean(
                                              leadFormTouched.workEmail &&
                                                leadFormErrors.workEmail
                                            )}
                                            fullWidth
                                            size="small"
                                          />
                                          {leadFormTouched.workEmail &&
                                          leadFormErrors.workEmail ? (
                                            <LeadFormErrorRow>
                                              <LeadFormErrorIcon />
                                              <LeadFormErrorText>
                                                {leadFormErrors.workEmail}
                                              </LeadFormErrorText>
                                            </LeadFormErrorRow>
                                          ) : null}
                                        </LeadFormField>

                                        <LeadFormField>
                                          <LeadFormFieldLabel htmlFor="phoneNumber">
                                            Phone Number
                                          </LeadFormFieldLabel>
                                          <LeadFormPhoneInput
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            defaultCountry="MY"
                                            international
                                            countryCallingCodeEditable={false}
                                            value={leadFormValues.phoneNumber}
                                            required
                                            className={
                                              hasPhoneNumberError
                                                ? "PhoneInput--error"
                                                : undefined
                                            }
                                            aria-invalid={hasPhoneNumberError}
                                            onChange={(value) => {
                                              setLeadFormValues((prev) => ({
                                                ...prev,
                                                phoneNumber: value,
                                              }));
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                phoneNumber: undefined,
                                              }));
                                            }}
                                            onBlur={() => {
                                              setLeadFormTouched((prev) => ({
                                                ...prev,
                                                phoneNumber: true,
                                              }));
                                              const phoneNumber =
                                                leadFormValues.phoneNumber;
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                phoneNumber: !phoneNumber
                                                  ? "This field is required."
                                                  : isValidPhoneNumber(
                                                      phoneNumber
                                                    )
                                                  ? undefined
                                                  : "Please enter a valid phone number.",
                                              }));
                                            }}
                                          />
                                          {leadFormTouched.phoneNumber &&
                                          leadFormErrors.phoneNumber ? (
                                            <LeadFormErrorRow>
                                              <LeadFormErrorIcon />
                                              <LeadFormErrorText>
                                                {leadFormErrors.phoneNumber}
                                              </LeadFormErrorText>
                                            </LeadFormErrorRow>
                                          ) : null}
                                        </LeadFormField>

                                        <LeadFormField>
                                          <LeadFormFieldLabel htmlFor="companyName">
                                            Company Name
                                          </LeadFormFieldLabel>
                                          <LeadFormTextField
                                            id="companyName"
                                            name="companyName"
                                            value={leadFormValues.companyName}
                                            onChange={(e) => {
                                              setLeadFormValues((prev) => ({
                                                ...prev,
                                                companyName: e.target.value,
                                              }));
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                companyName: undefined,
                                              }));
                                            }}
                                            onBlur={() => {
                                              setLeadFormTouched((prev) => ({
                                                ...prev,
                                                companyName: true,
                                              }));
                                              const companyName = (
                                                leadFormValues.companyName || ""
                                              ).trim();
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                companyName: companyName
                                                  ? undefined
                                                  : "This field is required.",
                                              }));
                                            }}
                                            required
                                            error={Boolean(
                                              leadFormTouched.companyName &&
                                                leadFormErrors.companyName
                                            )}
                                            fullWidth
                                            size="small"
                                          />
                                          {leadFormTouched.companyName &&
                                          leadFormErrors.companyName ? (
                                            <LeadFormErrorRow>
                                              <LeadFormErrorIcon />
                                              <LeadFormErrorText>
                                                {leadFormErrors.companyName}
                                              </LeadFormErrorText>
                                            </LeadFormErrorRow>
                                          ) : null}
                                        </LeadFormField>

                                        <LeadFormField>
                                          <LeadFormFieldLabel htmlFor="role">
                                            My role is a...
                                          </LeadFormFieldLabel>
                                          <LeadFormTextField
                                            id="role"
                                            name="role"
                                            select
                                            value={leadFormValues.role}
                                            onChange={(e) => {
                                              const nextRole = e.target.value;
                                              setLeadFormValues((prev) => ({
                                                ...prev,
                                                role: nextRole,
                                                roleOther:
                                                  nextRole === "other"
                                                    ? prev.roleOther
                                                    : "",
                                              }));
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                role: undefined,
                                                roleOther: undefined,
                                              }));
                                              if (nextRole !== "other") {
                                                setLeadFormTouched((prev) => ({
                                                  ...prev,
                                                  roleOther: false,
                                                }));
                                              }
                                            }}
                                            onBlur={() => {
                                              setLeadFormTouched((prev) => ({
                                                ...prev,
                                                role: true,
                                              }));
                                              const role = (
                                                leadFormValues.role || ""
                                              ).trim();
                                              setLeadFormErrors((prev) => ({
                                                ...prev,
                                                role: role
                                                  ? undefined
                                                  : "Please select an option.",
                                              }));
                                            }}
                                            required
                                            error={Boolean(
                                              leadFormTouched.role &&
                                                leadFormErrors.role
                                            )}
                                            fullWidth
                                            size="small"
                                            SelectProps={{
                                              displayEmpty: true,
                                              renderValue: (selected) => {
                                                const value = (
                                                  selected || ""
                                                ).toString();
                                                if (!value) {
                                                  return (
                                                    <span
                                                      style={{
                                                        color:
                                                          "rgba(0,0,0,0.38)",
                                                      }}
                                                    >
                                                      Please select a role
                                                    </span>
                                                  );
                                                }
                                                return (
                                                  leadFormRoleOptions.find(
                                                    (o) => o.value === value
                                                  )?.label || value
                                                );
                                              },
                                            }}
                                          >
                                            {leadFormRoleOptions.map(
                                              (option) => (
                                                <MenuItem
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </MenuItem>
                                              )
                                            )}
                                          </LeadFormTextField>
                                          {leadFormTouched.role &&
                                          leadFormErrors.role ? (
                                            <LeadFormErrorRow>
                                              <LeadFormErrorIcon />
                                              <LeadFormErrorText>
                                                {leadFormErrors.role}
                                              </LeadFormErrorText>
                                            </LeadFormErrorRow>
                                          ) : null}
                                        </LeadFormField>
                                        {isOtherRole ? (
                                          <LeadFormField>
                                            <LeadFormFieldLabel htmlFor="roleOther">
                                              Please specify
                                            </LeadFormFieldLabel>
                                            <LeadFormTextField
                                              id="roleOther"
                                              name="roleOther"
                                              value={leadFormValues.roleOther}
                                              onChange={(e) => {
                                                setLeadFormValues((prev) => ({
                                                  ...prev,
                                                  roleOther: e.target.value,
                                                }));
                                                setLeadFormErrors((prev) => ({
                                                  ...prev,
                                                  roleOther: undefined,
                                                }));
                                              }}
                                              onBlur={() => {
                                                setLeadFormTouched((prev) => ({
                                                  ...prev,
                                                  roleOther: true,
                                                }));
                                                const roleOther = (
                                                  leadFormValues.roleOther || ""
                                                ).trim();
                                                setLeadFormErrors((prev) => ({
                                                  ...prev,
                                                  roleOther: roleOther
                                                    ? undefined
                                                    : "This field is required.",
                                                }));
                                              }}
                                              required
                                              error={Boolean(
                                                leadFormTouched.roleOther &&
                                                  leadFormErrors.roleOther
                                              )}
                                              fullWidth
                                              size="small"
                                            />
                                            {leadFormTouched.roleOther &&
                                            leadFormErrors.roleOther ? (
                                              <LeadFormErrorRow>
                                                <LeadFormErrorIcon />
                                                <LeadFormErrorText>
                                                  {leadFormErrors.roleOther}
                                                </LeadFormErrorText>
                                              </LeadFormErrorRow>
                                            ) : null}
                                          </LeadFormField>
                                        ) : null}
                                      </LeadFormFields>

                                      <LeadFormSubmitButton
                                        type="submit"
                                        disabled={leadFormSubmitting}
                                        aria-busy={leadFormSubmitting}
                                      >
                                        <span>
                                          {leadFormSubmitting
                                            ? "Submitting"
                                            : leadFormSubmitLabel}
                                        </span>
                                        {leadFormSubmitting ? (
                                          <CircularProgress
                                            size={18}
                                            thickness={5}
                                            color="inherit"
                                          />
                                        ) : null}
                                      </LeadFormSubmitButton>
                                    </form>
                                  </>
                                )}
                              </LeadFormBody>
                            </LeadFormCard>

                            {quoteText ? (
                              <QuoteBlock>
                                <QuoteText>{quoteText}</QuoteText>
                                {quoteAuthor ? (
                                  <QuoteAuthor>{quoteAuthor}</QuoteAuthor>
                                ) : null}
                              </QuoteBlock>
                            ) : null}
                          </>
                        ) : null}
                      </DetailRight>
                    </DetailGrid>
                  </DetailSection>
                ) : null}
              </>
            ) : (
              <Card elevation={0}>
                <CardInner>
                  <NotFoundTitle>Report not found</NotFoundTitle>
                  <NotFoundDescription>
                    The Market Insight you’re looking for doesn’t exist (or the
                    link is invalid).
                  </NotFoundDescription>
                </CardInner>
              </Card>
            )}
          </Inner>
        </Main>
      </ToResolveClassNameError>
    </>
  );
}

export default MarketInsightDetailPage;
